<template>
  <div id="user-accounts-table">
    <v-data-table
      :items="data"
      :headers="headers"
      :loading="isLoading"
      class="body-2"
      show-select
      :options.sync="pagination"
      v-model="selectedUsers"
      item-key="id"
      :footer-props="footerProps"
      :items-per-page="paginationMeta.itemsPerPage"
      :no-data-text="$dictionary.app.common.users.noData"
      :server-items-length="paginationMeta.serverItemsLength"
      :loading-text="$dictionary.app.common.users.loading"
    >
      <template #item.created_at="{ item }">
        <table-matrics-row :text="formatDate(item.created_at)" />
      </template>
      <template #item.updated_at="{ item }">
        <table-matrics-row :text="formatDate(item.updated_at)" />
      </template>
      <template #item.role="{ item }">
        <table-matrics-row :text="capitalize(item.role)" />
      </template>
      <template #header.data-table-select="{ item, props, on }">
        <v-simple-checkbox
          v-ripple
          v-on="on"
          v-bind="props"
          color="primary"
          :value="props.value"
          indeterminate-icon="mdi-minus-box"
          :indeterminate="props.indeterminate"
          :class="{ 'selected-records-checkbox': props.indeterminate }"
        />
      </template>

      <template #item.data-table-select="{ item, isSelected, select }">
        <v-simple-checkbox
          color="primary"
          v-ripple
          :value="isSelected"
          :disabled="isOwner(item)"
          @input="select(!isSelected)"
          :class="{ 'cursor-not-allowed': isOwner(item) }"
        />
      </template>

      <template #item.email="{ item }">
        <table-matrics-row
          is-link
          is-text-pointer
          :text="item.email"
          text-class="blue--text"
          :navigate-to="`${$paths.users}/${item.id}/user`"
          :query="{ active_tab: 'profile' }"
        />
      </template>
      <template #item.name="{ item }">
        <table-matrics-row :text="fullName(item)" />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import headers from "@/data/table-headers/account-users.headers.json";
import { TableMatricsRow } from "@/components/shared";
import { capitalize } from "@/filters";

import { PaginationSortMixin } from "@/mixins";
import { getName as fullName, isEmpty } from "@/utils/common.utils";
import { ROLES } from "@/constants";

/**
 * Account users table listing
 */
export default {
  name: "AccountUsersTable",
  /**
   * ---------------- Custom events ------------------
   */
  emits: ["account-selected"],
  /**
   * ---------------- Mixins ------------------
   */
  mixins: [PaginationSortMixin],
  /**
   * ---------------- Props ------------------
   */
  props: {
    users: { type: Array, required: true, default: () => [] },
    isLoading: { type: Boolean, required: true, default: false },
    defaultSortOrder: { type: String, required: true, default: "" },
    resetSelectedAccounts: { type: Boolean, required: true, default: false },
  },
  /**
   * ---------------- Components ------------------
   */
  components: {
    TableMatricsRow,
  },
  /**
   * ---------------- Data properties ------------------
   */
  data() {
    return {
      selectedUsers: [],
      headers,
    };
  },
  /**
   * ---------------- Computed properties ------------------
   */
  computed: {
    /**
     * Users list
     * @type {Array}
     */
    data() {
      if (isEmpty(this.users)) return [];
      return this.users.map((user) => ({
        ...user,
        isSelectable: !this.isOwner(user),
      }));
    },
  },
  /**
   * ---------------- Watching properties ------------------
   */
  watch: {
    /**
     * Watches selectedUsers data property for its change
     */
    selectedUsers(newVal) {
      this.onAccountSelect(newVal);
    },
    /**
     * Reset selectedUsers model property value to empty array []
     */
    resetSelectedAccounts() {
      this.selectedUsers = [];
    },
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    fullName,
    capitalize,
    /**
     * Whether a role of an user is owner or not
     * @returns {Boolean} is owner or not
     */
    isOwner({ role }) {
      const { owner } = ROLES;
      return role.toLowerCase() === owner.toLowerCase();
    },
    /**
     * Emits custom event in parent component when a users gets selected
     * @param {Array} selectedUsers Selected users array
     * @emits account-selected
     */
    onAccountSelect(selectedUsers) {
      this.$emit("account-selected", selectedUsers);
    },
    /**
     * Formats date to am and pm format
     */
    formatDate(date) {
      const { formatDateToAmPm } = this.$options.filters;
      return formatDateToAmPm(date);
    },
  },
};
</script>
